/* eslint-disable indent */
/* eslint-disable no-shadow */
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
} from '@angular/core';
import { MatRadioChange } from '@angular/material/radio';
import { tableAnimation } from '@animations/table-animations';
import { LegView } from '@sequence/models/view/leg.view';
import {
  PassengerExpanded,
  StandbyListView,
} from '../models/standby-list.view';
import { StandbyListStore } from '../standby-list.store';
import { StandbyListInfoComponent } from '../standby-list-info/standby-list-info.component';
import { SabreTextDialogComponent } from '@shared/components/sabre-text-dialog/sabre-text-dialog.component';
import { Observable, of, tap } from 'rxjs';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { MatDialog } from '@angular/material/dialog';
import { SabreText } from '@shared/components/sabre-text-dialog/models/sabre-text';
import { DateTime } from 'luxon';
import { LoadStore } from '@flight-details/load/load.store';
import { LoadView } from '@flight-details/load/models/load.view';
import { Jumpseater } from '../models/jumpseater';
import { JumpseatService } from '../jumpseat.service';
import { CrewHistoryComponent } from '@flight-details/crew/crew-history/crew-history.component';
import { MemberLookupResponse } from '@shared/models/member-lookup.response';
import { MemberLookupService } from '@shared/services/member-lookup.service';
import {
  MsgType,
  NoContactDialogComponent,
} from '@shared/components/no-contact-dialog/no-contact-dialog.component';
import { ConnectionStatusService } from '@shared/services/connection-status.service';
import {
  OpenPhoneDialogComponent,
  OperationType,
} from '@shared/components/open-phone-dialog/open-phone-dialog.component';
import { SeatMapView } from '../models/seat-map.view';
import { SeatMapService } from '../seat-map.service';
import { SeatMapComponent } from '../seat-map/seat-map.component';

enum ContactType {
  Phone,
  Text,
}

@Component({
  selector: 'app-standby-list-card',
  templateUrl: './standby-list-card.component.html',
  styleUrls: ['./standby-list-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [tableAnimation],
})
export class StandbyListCardComponent implements OnChanges {
  standbyListStore = inject(StandbyListStore);
  loadStore = inject(LoadStore);
  jumpseatService = inject(JumpseatService);
  memberLookupService = inject(MemberLookupService);
  connectionStatusService = inject(ConnectionStatusService);
  dialog = inject(MatDialog);
  seatMapService = inject(SeatMapService);

  @Input() flightState?: LegView;

  load$?: Observable<RequestState<LoadView>>;
  standbyList$?: Observable<RequestState<StandbyListView>>;
  jumpseaters$?: Observable<Jumpseater[]>;
  jumpseatContact$?: Observable<RequestState<MemberLookupResponse>>;
  isJumpseatLoading$ = this.jumpseatService.isLoading$;
  listFilter$ = this.standbyListStore.listFilter$;
  connectionStatus$ = this.connectionStatusService.connectionStatus$;
  seatMap$?: Observable<RequestState<SeatMapView>>;

  contactIsLoadingId: string | null = null;

  // For referencing in template.
  standbyListInfoComponent = StandbyListInfoComponent;
  ContactType = ContactType;

  constructor() {}

  ngOnChanges(): void {
    this.loadData();
  }

  onExpand() {
    this.loadData();
  }

  refresh() {
    this.loadData(true);
  }

  onListFilterChange(event: MatRadioChange) {
    this.standbyListStore.setListFilter(event.value);
  }

  // For table animation.
  trackByName(_: number, passenger: PassengerExpanded): string {
    return `${passenger.firstName}${passenger.lastName}`;
  }

  onViewTextClick(text: string, timestamp: DateTime): void {
    const sabreText = new SabreText(text, timestamp.toISO());

    this.dialog.open(SabreTextDialogComponent, {
      data: {
        sabreText$: of(
          new RequestState<SabreText>(
            RequestStatus.IsCompleteWithResponse,
            sabreText,
          ),
        ),
      },
      maxWidth: '100vw',
      maxHeight: '95vh',
    });
  }

  private loadData(isRefresh = false): void {
    if (this.flightState) {
      this.standbyList$ = this.standbyListStore.getFilteredStandbyList(
        this.flightState,
        isRefresh,
      );

      this.load$ = this.loadStore.getLoad(this.flightState, isRefresh);

      const request = this.flightState?.getStandbyListRequest();

      if (request) {
        this.jumpseaters$ = this.jumpseatService.getJumpseatList(
          request,
          isRefresh,
        );
      }
    }
  }

  onSeatMapClick(): void {
    this.dialog.open(SeatMapComponent, {
      data: {
        flightState: this.flightState,
      },
      maxHeight: '95vh',
      maxWidth: '100vw',
    });
  }

  onHistoryClick(jumpseater: Jumpseater): void {
    this.dialog.open(CrewHistoryComponent, {
      data: {
        pilot: null,
        jumpseater,
      },
      maxHeight: '95vh',
      maxWidth: '100vw',
    });
  }

  onContactClick(jumpseater: Jumpseater, type: ContactType): void {
    this.contactIsLoadingId = this.getContactId(jumpseater, type);

    this.jumpseatContact$ = this.memberLookupService
      .getContactInfo(jumpseater.empNumber)
      .pipe(
        tap((response) => {
          if (!response.isLoading) {
            this.contactIsLoadingId = null;
          }

          if (response.response) {
            const phoneNumber =
              response.response.cell?.trim().replace(/\D/g, '') ||
              response.response.telephone?.trim().replace(/\D/g, '');

            if (phoneNumber) {
              switch (type) {
                case ContactType.Phone:
                  this.dialog.open(OpenPhoneDialogComponent, {
                    data: {
                      type: OperationType.Phone,
                      code: `tel:${phoneNumber}`,
                    },
                  });
                  // window.open(`tel:${phoneNumber}`, '_');
                  break;
                case ContactType.Text:
                  this.dialog.open(OpenPhoneDialogComponent, {
                    data: {
                      type: OperationType.Text,
                      code: `sms:${phoneNumber}`,
                    },
                  });
                  // window.open(`sms:${phoneNumber}`, '_');
                  break;
                default:
              }
            } else {
              this.dialog.open(NoContactDialogComponent, {
                data: MsgType.Individual,
              });
            }
          }
        }),
      );
  }

  getContactId(jumpseater: Jumpseater, type: ContactType): string {
    return `${jumpseater.empNumber}-${type}`;
  }

  contactIsLoading(jumpseater: Jumpseater, type: ContactType): boolean {
    return this.contactIsLoadingId === this.getContactId(jumpseater, type);
  }
}
